import React, { useState, useCallback, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { TileLayer, useMap, useMapEvents } from 'react-leaflet';
import WmsLayerComponent from './wmsLayer';
import LayerControl from './LayerControl';
import api_service from '../../api/api_service';
import Legend from './Legend';
import L from 'leaflet'; // Keep L for other potential uses
import ReactDOM from 'react-dom/client';

const baseTileLayerUrl = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}";
const baseTileLayerAttribution = 'Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012';

const geoserverWmsUrl = "/geoserver/lira/wms";
// const geoserverWmsUrl = "http://localhost:8083/geoserver/lira/wms";ß

const LeafletLegendControl = ({ styleDefinition }) => {
    const map = useMap();

    useEffect(() => {
        const CustomLegendControl = L.Control.extend({
            onAdd: function(map) {
                this._div = L.DomUtil.create('div', 'leaflet-control leaflet-bar leaflet-control-custom-legend');
                this._div.className += " p-2 rounded-lg shadow-lg lira-legend";

                const root = ReactDOM.createRoot(this._div);
                root.render(<Legend styleDefinition={styleDefinition} />);
                this.root = root;

                L.DomEvent.disableClickPropagation(this._div);
                L.DomEvent.disableScrollPropagation(this._div);

                return this._div;
            },
            onRemove: function(map) {
                if (this.root) {
                    // Use a microtask to prevent React from complaining about unmounting a node
                    // that is already gone from the DOM.
                    queueMicrotask(() => this.root.unmount());
                }

                L.DomEvent.disableClickPropagation(this._div);
                L.DomEvent.disableScrollPropagation(this._div);

                return this._div;
            },
        });

        const legendControl = new CustomLegendControl({ position: 'bottomright' });
        legendControl.addTo(map);

        return () => {
            legendControl.remove();
        };
    }, [map, styleDefinition]);

    return null;
};


const LayerManagerComponent = forwardRef(({ onActiveQueryableLayersChange }, ref) => {
    const [allLayerConfigs, setAllLayerConfigs] = useState({});
    const [layerVisibility, setLayerVisibility] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [transparencyMode, setTransparencyMode] = useState(false);
    const opacity = useMemo(() => (transparencyMode ? 0.5 : 1), [transparencyMode]);

    useImperativeHandle(ref, () => ({
        clearSelection: () => {
            setLayerVisibility(prev => {
                const newVisibility = {};
                Object.keys(prev).forEach(key => newVisibility[key] = false);
                return newVisibility;
            });
        }
    }));

    useEffect(() => {
        const fetchLayers = async () => {
            try {
                setIsLoading(true);
                const layersArray = await api_service.getGlobalLayers();

                const configs = layersArray.reduce((acc, layer) => {
                    const { layer_id: id, group, ...rest } = layer;
                    if (id === undefined) {
                        console.warn("LayerManager: Layer object is missing 'layer_id' or it's undefined. Layer data:", layer);
                        return acc;
                    }
                    const wmsParams = {
                        layers: `lira:${rest.layer_name}`,
                        format: rest.format,
                        transparent: true,
                        version: '1.1.0',
                    };
                    acc[id] = {
                        ...rest,
                        url: geoserverWmsUrl,
                        group_name: group,
                        wmsParams: wmsParams
                    };
                    return acc;
                }, {});
                setAllLayerConfigs(configs); // Update all layer configurations
                const initialVisibility = Object.entries(configs).reduce((acc, [key, config]) => {
                    acc[key] = !!config.initialVisibility;
                    return acc;
                }, {});
                setLayerVisibility(initialVisibility);
                setError(null);
            } catch (err) {
                console.error("Failed to fetch layers:", err);
                setError(err.message || "Failed to load layers. Please try again later.");
                setAllLayerConfigs({});
                setLayerVisibility({});
            } finally {
                setIsLoading(false);
            }
        };
        fetchLayers();
    }, []);

    useEffect(() => {
        if (onActiveQueryableLayersChange && Object.keys(allLayerConfigs).length > 0) {
            const activeLayers = Object.entries(allLayerConfigs)
                .filter(([key, config]) => layerVisibility[key] && config.is_queryable)
                .map(([key, config]) => ({ id: key, ...config }))
                .sort((a, b) => (b.z_index || 0) - (a.z_index || 0));
            onActiveQueryableLayersChange(activeLayers);
        }
    }, [layerVisibility, onActiveQueryableLayersChange, allLayerConfigs]);

    const handleVisibilityChange = useCallback((selectedLayerId) => {
        setLayerVisibility(prevVisibility => {
            const newVisibility = {};
            Object.keys(prevVisibility).forEach(id => {
                newVisibility[id] = id === selectedLayerId;
            });
            return newVisibility;
        });
    }, []);

    const controllableLayersForControl = useMemo(() => {
        const grouped = {};
        Object.entries(allLayerConfigs).forEach(([key, config]) => {
            if (config.fe_layer_name) {
                const group = config.group_name || 'General';
                const displayGroup = group === 'INPUT' ? 'INPUT LAYERS' : group === 'OUTPUT' ? 'OUTPUT MAPS' : group;
                if (!grouped[displayGroup]) {
                    grouped[displayGroup] = [];
                }
                grouped[displayGroup].push({ id: key, name: config.fe_layer_name, description: config.description });
            }
        });
        
        return Object.entries(grouped);
    }, [allLayerConfigs]);

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen text-lg">Loading layers...</div>;
    }
    if (error) {
        return <div className="p-5 text-red-600 bg-red-100 rounded-md m-4">Error: {error}</div>;
    }

    return (
        <>
            <TileLayer
                attribution={baseTileLayerAttribution}
                url={baseTileLayerUrl}
                zIndex={0}
            />

            {Object.entries(allLayerConfigs).map(([key, config]) => (
                <React.Fragment key={key}>
                    {layerVisibility[key] && (
                        <>
                            <WmsLayerComponent
                                opacity={opacity}
                                url={config.url}
                                options={config.wmsParams}
                                zIndex={config.z_index}
                            />
                            {config.style_definition && (
                                <LeafletLegendControl styleDefinition={config.style_definition} />
                            )}
                        </>
                    )}
                </React.Fragment>
            ))}            
            {/* Cadastral layer */}
            {layerVisibility['cadastral_map'] && (
                <TileLayer
                    attribution='Cyprus Cadastral Map &copy; Department of Lands and Surveys'
                    url="https://eservices.dls.moi.gov.cy/arcgis/rest/services/National/CadastralMap_EN/MapServer/tile/{z}/{y}/{x}"
                    opacity={0.5}
                    zIndex={1}
                />
            )}            {Object.keys(controllableLayersForControl).length > 0 && (
                <LayerControl
                    layers={controllableLayersForControl}
                    layerVisibility={layerVisibility}
                    onVisibilityChange={handleVisibilityChange}
                    transparencyMode={transparencyMode}
                    onTransparencyChange={setTransparencyMode}
                />
            )}
        </>
    );
});

export default React.memo(LayerManagerComponent);
